import React from 'react';
import { Navigate } from 'react-router-dom';
import { isAdmin, isSuperAdmin } from '../../utils/roleConstants';
import { Form, FormGroup, FormControl, Button, Row, Col, Alert, Card, Spinner } from 'react-bootstrap';
import config from '../../../config';
import {
    buildYearOptions,
    DEFAULT_MONTHLY_TARGET,
    ensureYearMonths,
    loadMonthlyTargetsFromAccount,
    MONTH_LABELS,
    TARGET_CATEGORY,
    cacheMonthlyTargetsInLocalStorage,
} from '../../utils/monthlyTargets';

class Targets extends React.Component {
    constructor(props) {
        super(props);
        const currentYear = new Date().getFullYear();
        this.state = {
            user: null,
            selectedYear: currentYear,
            selectedCategory: TARGET_CATEGORY.OEE,
            monthlyOEE: {},
            monthlyProductivity: {},
            loading: true,
            saving: false,
            success: false,
            error: '',
            yearOptions: buildYearOptions(currentYear),
        };
    }

    componentDidMount() {
        try {
            const userData = localStorage.getItem('user');
            if (!userData) return;

            const user = JSON.parse(userData);
            if (!isAdmin(user) && !isSuperAdmin(user)) return;

            this.setState({ user }, this.loadTargets);
        } catch (error) {
            this.setState({ error: 'Failed to load user data', loading: false });
        }
    }

    loadTargets = () => {
        const { user } = this.state;
        if (!user || !user.accountId) {
            this.setState({ loading: false, error: 'User information not found. Please log in again.' });
            return;
        }

        this.setState({ loading: true, error: '' });
        fetch(`${config.apiUrl}/getAccountTarget?account_id=${user.accountId}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        })
            .then((res) => (res.ok ? res.json() : null))
            .then((account) => {
                const { oee, productivity } = loadMonthlyTargetsFromAccount(account || {});
                const { selectedYear } = this.state;
                this.setState({
                    monthlyOEE: ensureYearMonths(oee, selectedYear),
                    monthlyProductivity: ensureYearMonths(productivity, selectedYear),
                    loading: false,
                });
                cacheMonthlyTargetsInLocalStorage(
                    ensureYearMonths(oee, selectedYear),
                    ensureYearMonths(productivity, selectedYear)
                );
            })
            .catch(() => {
                this.setState({ loading: false, error: 'Failed to load targets.' });
            });
    };

    getActiveMap = () => {
        const { selectedCategory, monthlyOEE, monthlyProductivity } = this.state;
        return selectedCategory === TARGET_CATEGORY.PRODUCTIVITY
            ? monthlyProductivity
            : monthlyOEE;
    };

    getActiveKey = () => (
        this.state.selectedCategory === TARGET_CATEGORY.PRODUCTIVITY
            ? 'monthlyProductivity'
            : 'monthlyOEE'
    );

    handleYearChange = (e) => {
        const selectedYear = parseInt(e.target.value, 10);
        this.setState((prev) => ({
            selectedYear,
            monthlyOEE: ensureYearMonths(prev.monthlyOEE, selectedYear),
            monthlyProductivity: ensureYearMonths(prev.monthlyProductivity, selectedYear),
            success: false,
            error: '',
        }));
    };

    handleCategoryChange = (e) => {
        this.setState({
            selectedCategory: e.target.value,
            success: false,
            error: '',
        });
    };

    handleMonthChange = (month, value) => {
        const { selectedYear } = this.state;
        const yearKey = String(selectedYear);
        const activeKey = this.getActiveKey();
        const num = value === '' ? '' : parseFloat(value);

        this.setState((prev) => {
            const map = { ...prev[activeKey] };
            const months = { ...(map[yearKey] || {}) };
            months[month] = value === '' ? '' : (Number.isFinite(num) ? num : DEFAULT_MONTHLY_TARGET);
            map[yearKey] = months;
            return { [activeKey]: map, success: false, error: '' };
        });
    };

    handleSave = () => {
        const { user, monthlyOEE, monthlyProductivity, selectedYear } = this.state;
        if (!user || !user.accountId) {
            this.setState({ error: 'User information not found. Please log in again.' });
            return;
        }

        const normalizedOEE = ensureYearMonths(monthlyOEE, selectedYear);
        const normalizedProductivity = ensureYearMonths(monthlyProductivity, selectedYear);

        this.setState({ saving: true, success: false, error: '' });

        fetch(`${config.apiUrl}/updateTarget?id=${user.accountId}`, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                monthlyOEE: normalizedOEE,
                monthlyProductivity: normalizedProductivity,
            }),
        })
            .then((response) => {
                if (response.status !== 200) {
                    return response.text().then((text) => {
                        throw new Error(text || 'Failed to update targets');
                    });
                }
                cacheMonthlyTargetsInLocalStorage(normalizedOEE, normalizedProductivity);
                this.setState({
                    monthlyOEE: normalizedOEE,
                    monthlyProductivity: normalizedProductivity,
                    saving: false,
                    success: true,
                });
                setTimeout(() => this.setState({ success: false }), 3000);
            })
            .catch((error) => {
                this.setState({
                    saving: false,
                    error: error.message || 'Failed to update targets. Please try again.',
                });
            });
    };

    handleCancel = () => {
        this.loadTargets();
    };

    renderMonthGrid() {
        const { selectedYear } = this.state;
        const activeMap = this.getActiveMap();
        const yearKey = String(selectedYear);
        const months = activeMap[yearKey] || {};

        return (
            <Row>
                {MONTH_LABELS.map(({ key, label }) => (
                    <Col key={key} xs={6} md={4} lg={3} className="mb-3">
                        <FormGroup>
                            <Form.Label>{label}</Form.Label>
                            <FormControl
                                type="number"
                                min="0"
                                step="0.1"
                                value={months[key] ?? DEFAULT_MONTHLY_TARGET}
                                onChange={(e) => this.handleMonthChange(key, e.target.value)}
                            />
                        </FormGroup>
                    </Col>
                ))}
            </Row>
        );
    }

    render() {
        try {
            const userData = localStorage.getItem('user');
            if (!userData) return <Navigate to="/login" replace />;

            const currentUser = JSON.parse(userData);
            if (!isAdmin(currentUser) && !isSuperAdmin(currentUser)) {
                return (
                    <div className="container">
                        <div style={{ marginTop: '20px', padding: '20px', textAlign: 'center' }}>
                            <h4>Access Denied</h4>
                            <p>You do not have permission to access Target Settings. This feature is only available for administrators.</p>
                        </div>
                    </div>
                );
            }
        } catch (error) {
            return <Navigate to="/login" replace />;
        }

        const {
            selectedYear,
            selectedCategory,
            yearOptions,
            loading,
            saving,
            success,
            error,
        } = this.state;

        return (
            <div className="container">
                <div style={{ marginTop: '20px', maxWidth: '960px' }}>
                    <div style={{ marginBottom: '24px' }}>
                        <h4>Target Settings</h4>
                        <p className="text-muted">
                            Set monthly OEE and Productivity targets by year. Unset values default to {DEFAULT_MONTHLY_TARGET}%.
                        </p>
                    </div>

                    {success && (
                        <Alert variant="success" onClose={() => this.setState({ success: false })} dismissible>
                            Targets updated successfully!
                        </Alert>
                    )}

                    {error && (
                        <Alert variant="danger" onClose={() => this.setState({ error: '' })} dismissible>
                            {error}
                        </Alert>
                    )}

                    <Card className="mb-3">
                        <Card.Body>
                            <Row>
                                <Col md={6} className="mb-3 mb-md-0">
                                    <FormGroup>
                                        <Form.Label>Year</Form.Label>
                                        <FormControl
                                            as="select"
                                            value={selectedYear}
                                            onChange={this.handleYearChange}
                                            disabled={loading || saving}
                                        >
                                            {yearOptions.map((year) => (
                                                <option key={year} value={year}>{year}</option>
                                            ))}
                                        </FormControl>
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Form.Label>Category</Form.Label>
                                        <FormControl
                                            as="select"
                                            value={selectedCategory}
                                            onChange={this.handleCategoryChange}
                                            disabled={loading || saving}
                                        >
                                            <option value={TARGET_CATEGORY.OEE}>OEE</option>
                                            <option value={TARGET_CATEGORY.PRODUCTIVITY}>Productivity</option>
                                        </FormControl>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" size="sm" /> Loading targets…
                        </div>
                    ) : (
                        <Card className="mb-3">
                            <Card.Body>
                                <h5 className="mb-3">
                                    {selectedCategory === TARGET_CATEGORY.OEE ? 'OEE' : 'Productivity'} targets — {selectedYear}
                                </h5>
                                {this.renderMonthGrid()}
                            </Card.Body>
                        </Card>
                    )}

                    <div style={{ display: 'flex', gap: '10px' }}>
                        <Button variant="primary" onClick={this.handleSave} disabled={loading || saving}>
                            {saving ? 'Saving…' : 'Save Changes'}
                        </Button>
                        <Button variant="secondary" onClick={this.handleCancel} disabled={loading || saving}>
                            Cancel
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
}

export { Targets };
